import ApiClientOpen from '../api/axios/apiClientOpen';
import type { ClienteListResponse, ClienteResponse, ClienteRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const CLIENTE_ENDPOINT = '/clientes';

interface GetAllClientesParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for Cliente entity operations
 */
class ClienteService {
  /**
   * Get all clientes with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of clientes and metadata
   */
  async getAllClientes(params?: GetAllClientesParams): Promise<ClienteListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<ClienteListResponse>(`${CLIENTE_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching clientes:', error);
      throw error;
    }
  }

  /** 
   * Get all clientes with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of clientes and metadata
   */
  async getAll(params: GetAllParams): Promise<ClienteListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } },
            { email: { $contains: filter } },
            { telefono: { $contains: filter } },
            { razonSocial: { $contains: filter } }
          ]
        };
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<ClienteListResponse>(`${CLIENTE_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching clientes:', error);
      throw error;
    }
  }

  /**
   * Get a cliente by ID with optional population and field selection
   * @param id Cliente ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with cliente data
   */
  async getClienteById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<ClienteResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<ClienteResponse>(`${CLIENTE_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cliente with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new cliente
   * @param clienteData Cliente data to create
   * @returns Promise with created cliente data
   */
  async createCliente(clienteData: ClienteRequest): Promise<ClienteResponse> {
    try {
      const response = await ApiClientOpen.post<ClienteResponse>(CLIENTE_ENDPOINT, clienteData);
      return response.data;
    } catch (error) {
      console.error('Error creating cliente:', error);
      throw error;
    }
  }

  /**
   * Create a new cliente (alias method)
   * @param clienteData Cliente data to create
   * @returns Promise with created cliente data
   */
  async create(clienteData: ClienteRequest): Promise<ClienteResponse> {
    return this.createCliente(clienteData);
  }

  /**
   * Update an existing cliente
   * @param id Cliente ID to update
   * @param clienteData Updated cliente data
   * @returns Promise with updated cliente data
   */
  async updateCliente(id: number | string, clienteData: ClienteRequest): Promise<ClienteResponse> {
    try {
      const response = await ApiClientOpen.put<ClienteResponse>(`${CLIENTE_ENDPOINT}/${id}`, clienteData);
      return response.data;
    } catch (error) {
      console.error(`Error updating cliente with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing cliente (alias method)
   * @param id Cliente ID to update
   * @param clienteData Updated cliente data
   * @returns Promise with updated cliente data
   */
  async update(id: number | string, clienteData: ClienteRequest): Promise<ClienteResponse> {
    return this.updateCliente(id, clienteData);
  }

  /**
   * Delete a cliente by ID (alias method)
   * @param id Cliente ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteCliente(id);
  }

  /**
   * Delete a cliente by ID
   * @param id Cliente ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteCliente(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${CLIENTE_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting cliente with id ${id}:`, error);
      throw error;
    }
  }
}

export default new ClienteService();